import { useEffect, useMemo, useState } from "react";
import api, { fmtMXN, fmtMXNFull } from "@/lib/api";
import { ChevronLeft, ChevronRight, Zap, Banknote, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DIVISORS = { semanal: 6, quincenal: 15, mensual: 30 };
const CYCLE = [null, "completo", "medio", "falta", "extra"];
const STYLE = {
  completo: { bg: "rgba(34,197,94,0.28)", border: "var(--green)", color: "#86efac", short: "C" },
  medio: { bg: "rgba(251,191,36,0.25)", border: "#fbbf24", color: "#fde68a", short: "½" },
  falta: { bg: "rgba(239,68,68,0.25)", border: "var(--red)", color: "#fca5a5", short: "F" },
  extra: { bg: "rgba(167,139,250,0.28)", border: "#a78bfa", color: "#c4b5fd", short: "E" },
};
const WEEK = ["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"];

const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const dayValue = (e) => Math.round((Number(e.base_salary) || 0) / (DIVISORS[e.payment_period] || 15) * 100) / 100;
const WEIGHT = { completo: 1, medio: 0.5, extra: 1, falta: 0 };

export default function TeamWorkdays({ employees, projects, canWrite, canPay = false }) {
  const [filter, setFilter] = useState("all"); // all | admin | project_id
  const [monday, setMonday] = useState(mondayOf(new Date()));
  const [marks, setMarks] = useState({}); // `${empId}|${date}` -> type
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(d.getDate() + i); return isoOf(d);
  }), [monday]);

  const list = employees.filter((e) => e.active !== false).filter((e) => {
    if (filter === "all") return true;
    if (filter === "admin") return e.payroll_type === "administrativa";
    return e.payroll_type === "obra" && e.project_id === filter;
  });

  const load = async () => {
    const { data } = await api.get("/workdays/team", { params: { start: days[0], end: days[6] } });
    const map = {};
    data.forEach((r) => { map[`${r.employee_id}|${r.date}`] = r.day_type; });
    setMarks(map);
  };
  useEffect(() => { load(); }, [days[0]]); // eslint-disable-line

  const cycle = async (empId, date) => {
    if (!canWrite) return;
    const key = `${empId}|${date}`;
    const next = CYCLE[(CYCLE.indexOf(marks[key] || null) + 1) % CYCLE.length];
    setMarks((m) => ({ ...m, [key]: next }));
    try {
      await api.put("/workdays", { employee_id: empId, date, day_type: next });
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); load(); }
  };

  const bulk = async (marksArr, msg) => {
    setSaving(true);
    try {
      await api.post("/workdays/bulk", { marks: marksArr });
      toast.success(msg);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al marcar"); }
    finally { setSaving(false); }
  };

  // Semana completa (L-S) para un empleado
  const fullWeek = (empId) => bulk(days.slice(0, 6).map((date) => ({ employee_id: empId, date, day_type: "completo" })), "Semana completa marcada");
  // Un día completo para todos los listados
  const allDay = (date) => bulk(list.map((e) => ({ employee_id: e.id, date, day_type: "completo" })), "Día marcado para todo el equipo");

  const weekTotal = (e) => {
    const dv = dayValue(e);
    const paid = days.reduce((s, d) => s + (WEIGHT[marks[`${e.id}|${d}`]] || 0), 0);
    return { paid, amount: Math.round(paid * dv * 100) / 100 };
  };
  const grandTotal = list.reduce((s, e) => s + weekTotal(e).amount, 0);
  const withDays = list.filter((e) => weekTotal(e).paid > 0);
  const fmtHead = (d) => `${Number(d.slice(8, 10))}`;

  const generatePayroll = async () => {
    if (!withDays.length) { toast.error("Nadie tiene días marcados esta semana"); return; }
    if (!window.confirm(`¿Generar la nómina de la semana ${days[0].slice(8)}/${days[0].slice(5, 7)} → ${days[6].slice(8)}/${days[6].slice(5, 7)} para ${withDays.length} trabajador${withDays.length === 1 ? "" : "es"} (${fmtMXNFull(grandTotal)})? Se crean como borrador; los que ya tengan pago en el período se omiten.`)) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/payroll/generate-week", {
        start: days[0], end: days[6], employee_ids: withDays.map((e) => e.id),
      });
      const c = data.created.length, s = data.skipped.length;
      if (c) toast.success(`${c} pago${c === 1 ? "" : "s"} de nómina generado${c === 1 ? "" : "s"} (borrador)${s ? ` · ${s} omitido${s === 1 ? "" : "s"}` : ""}`);
      else toast.warning(`No se generó ningún pago${s ? `: ${data.skipped[0]?.reason}` : ""}`);
      if (s) data.skipped.slice(0, 3).forEach((k) => toast.info(`${k.employee}: ${k.reason}`));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al generar la nómina");
    } finally { setGenerating(false); }
  };

  return (
    <div className="space-y-3" data-testid="team-workdays">
      <div className="panel p-3 flex flex-wrap items-center gap-3">
        <select data-testid="team-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="input-cyber px-3 py-2 text-sm min-w-[220px]">
          <option value="all" style={{ background: "#0a0f1d" }}>Todos los trabajadores</option>
          <option value="admin" style={{ background: "#0a0f1d" }}>Administrativos</option>
          {projects.map((p) => <option key={p.id} value={p.id} style={{ background: "#0a0f1d" }}>{`Obra: ${p.code} · ${p.name}`}</option>)}
        </select>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          {canPay && (
            <button data-testid="generate-week-payroll" onClick={generatePayroll} disabled={generating || withDays.length === 0}
              className="btn-primary px-3 py-2 rounded-sm text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1.5 disabled:opacity-50"
              title="Crea un pago de nómina (borrador) por cada trabajador con días marcados esta semana">
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Banknote className="w-3.5 h-3.5" />} Generar nómina de la semana
            </button>
          )}
          <div className="flex items-center gap-1">
            <button data-testid="team-prev" onClick={() => setMonday((m) => { const x = new Date(m); x.setDate(x.getDate() - 7); return x; })} className="btn-ghost p-1.5 rounded-sm"><ChevronLeft className="w-4 h-4" /></button>
            <span className="font-display font-bold text-slate-100 text-sm w-48 text-center" data-testid="team-week">Semana {days[0].slice(8)}/{days[0].slice(5, 7)} → {days[6].slice(8)}/{days[6].slice(5, 7)}</span>
            <button data-testid="team-next" onClick={() => setMonday((m) => { const x = new Date(m); x.setDate(x.getDate() + 7); return x; })} className="btn-ghost p-1.5 rounded-sm"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[860px]" data-testid="team-table">
            <thead className="panel-header">
              <tr>
                <th className="label-mono px-3 py-2.5 text-left">Trabajador</th>
                {days.map((d, i) => (
                  <th key={d} className="px-1 py-2 text-center" style={{ width: 52 }}>
                    <div className="label-mono text-[8px]" style={{ color: i === 6 ? "#a78bfa" : "var(--text-mute)" }}>{WEEK[i]}</div>
                    {canWrite ? (
                      <button data-testid={`team-allday-${d}`} onClick={() => allDay(d)} disabled={saving} title="Marcar día completo a todo el equipo"
                        className="font-mono font-bold text-slate-100 hover:text-cyan-300">{fmtHead(d)}</button>
                    ) : <span className="font-mono font-bold text-slate-100">{fmtHead(d)}</span>}
                  </th>
                ))}
                <th className="label-mono px-3 py-2.5 text-right">Semana</th>
                {canWrite && <th className="px-2 py-2.5" style={{ width: 40 }} />}
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-slate-500">Sin trabajadores en este filtro.</td></tr>}
              {list.map((e) => {
                const wt = weekTotal(e);
                return (
                  <tr key={e.id} data-testid={`team-row-${e.id}`} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td className="px-3 py-1.5">
                      <div className="font-semibold text-slate-100">{e.name}</div>
                      <div className="text-[9px] text-slate-500">{e.position} · día {fmtMXN(dayValue(e))}</div>
                    </td>
                    {days.map((d) => {
                      const t = marks[`${e.id}|${d}`];
                      const st = STYLE[t];
                      return (
                        <td key={d} className="px-0.5 py-1 text-center">
                          <button data-testid={`team-cell-${e.id}-${d}`} onClick={() => cycle(e.id, d)} disabled={!canWrite}
                            className="w-10 h-9 rounded-sm font-mono font-bold text-sm transition-colors disabled:cursor-default"
                            style={st ? { background: st.bg, border: `1px solid ${st.border}`, color: st.color }
                              : { background: "var(--bg-2)", border: "1px solid var(--border-2)", color: "var(--text-mute)" }}
                            title={t || "Sin marcar"}>
                            {st ? st.short : "·"}
                          </button>
                        </td>
                      );
                    })}
                    <td className="px-3 py-1.5 text-right">
                      <div className="font-mono font-bold" style={{ color: "var(--cyan)" }} data-testid={`team-total-${e.id}`}>{fmtMXNFull(wt.amount)}</div>
                      <div className="text-[9px] text-slate-500">{wt.paid} día{wt.paid === 1 ? "" : "s"}</div>
                    </td>
                    {canWrite && (
                      <td className="px-2 py-1.5 text-center">
                        <button data-testid={`team-fullweek-${e.id}`} onClick={() => fullWeek(e.id)} disabled={saving} title="Marcar semana completa (L-S)"
                          className="btn-ghost p-1.5 rounded-sm" style={{ color: "var(--green)" }}><Zap className="w-3.5 h-3.5" /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {list.length > 0 && (
              <tfoot>
                <tr style={{ borderTop: "1px solid var(--border-2)" }}>
                  <td className="px-3 py-2 label-mono text-right" colSpan={8}>TOTAL SEMANA ({list.length} trabajadores)</td>
                  <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: "var(--cyan)" }} data-testid="team-grand-total">{fmtMXNFull(grandTotal)}</td>
                  {canWrite && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="px-3 py-2 flex flex-wrap gap-3 text-[10px]" style={{ borderTop: "1px solid var(--border)", color: "var(--text-dim)" }}>
          <span><b style={{ color: "#86efac" }}>C</b> completo</span>
          <span><b style={{ color: "#fde68a" }}>½</b> medio día</span>
          <span><b style={{ color: "#fca5a5" }}>F</b> falta</span>
          <span><b style={{ color: "#c4b5fd" }}>E</b> extra</span>
          {canWrite && <span className="ml-auto">⚡ marca semana completa (L-S) · clic en el número del día lo marca a todo el equipo</span>}
        </div>
      </div>
    </div>
  );
}
